import logger from '@/lib/edge-logger';

// 1. 类型定义
/**
 * 模块导入类型，支持动态导入函数或代码字符串
 * @typedef {Function|String} ModuleImport
 */
type ModuleImport = (() => Promise<any>) | string;

/**
 * 模块配置对象，键为模块名称，值为模块导入方式
 * @typedef {Object.<string, ModuleImport>} ModuleConfig
 */
type ModuleConfig = {
  [moduleName: string]: ModuleImport; // 模块名: 动态导入函数或代码字符串
};

/**
 * 列配置中的模块需求声明
 * @typedef {Array<string>} RequiredModules
 */
type RequiredModules = string[];

/**
 * 列配置中的嵌入式自定义模块定义
 * @typedef {Object.<string, string>} EmbeddedModules
 */
type EmbeddedModules = {
  [moduleName: string]: string; // 模块名: 代码字符串
};

// 2. 默认模块配置（可根据需要扩展，如添加lodash等）
/**
 * 默认模块配置
 * @type {ModuleConfig}
 * 
 * @example
 * // 添加其他模块
 * // lodash: () => import('lodash'),
 * // 使用代码字符串
 * // customModule: '({ customFunction: (x) => x * 2 })',
 */
const defaultModuleConfig: ModuleConfig = {
  dayjs: () => import('dayjs'), // 动态导入dayjs
  // 示例：添加其他模块
  // lodash: () => import('lodash'),
  // 示例：使用代码字符串
  // customModule: '({ customFunction: (x) => x * 2 })',
};

/**
 * 使用示例
 * 
 * @example
 * // 基本用法
 * import { parseColumns } from './dynamic-function-render';
 * 
 * const rawColumns = [
 *   {
 *     title: '创建时间',
 *     dataIndex: 'createdAt',
 *     render: 'value => dayjs(value).format("YYYY-MM-DD HH:mm:ss")',
 *     requiredModules: ['dayjs']
 *   }
 * ];
 * 
 * const columns = await parseColumns(rawColumns);
 * 
 * @example
 * // 使用自定义模块
 * import { parseColumns } from './dynamic-function-render';
 * 
 * const rawColumns = [
 *   {
 *     title: '价格',
 *     dataIndex: 'price',
 *     render: 'value => lodash.round(value, 2)',
 *     requiredModules: ['lodash']
 *   }
 * ];
 * 
 * const customModuleConfig = {
 *   lodash: () => import('lodash'),
 *   utils: '({ formatCurrency: (value) => `$${value.toFixed(2)}` })'
 * };
 * 
 * const columns = await parseColumns(rawColumns, customModuleConfig);
 * 
 * @example
 * // 清除缓存
 * import { clearModuleCache } from './dynamic-function-render';
 * 
 * clearModuleCache();
 */

// 3. 模块缓存
/**
 * 模块缓存对象，用于存储已加载的模块
 * @type {Object.<string, any>}
 */
const moduleCache: { [key: string]: any } = {};

// 4. 模块缓存键生成函数
/**
 * 生成模块配置的缓存键
 * @param {ModuleConfig} moduleConfig - 模块配置对象
 * @returns {string} 缓存键
 */
const _generateCacheKey = (moduleConfig: ModuleConfig): string => {
  // 优化键生成逻辑，提高效率
  return Object.keys(moduleConfig)
    .sort()
    .map(key => {
      const value = moduleConfig[key];
      // 对于函数类型，使用占位符；对于字符串类型，直接使用值
      return `${key}:${typeof value === 'string' ? value : '[function]'}`;
    })
    .join('|');
};

/**
 * 加载所有需要的模块
 * @param {ModuleConfig} mergedModules - 合并后的模块配置
 * @returns {Promise<Object>} 模块导出对象
 * 
 * @example
 * const modules = await _loadModules({
 *   dayjs: () => import('dayjs'),
 *   customModule: '({ customFunction: (x) => x * 2 })'
 * });
 */
const _loadModules = async (mergedModules: ModuleConfig) => {
  // 生成缓存键
  const cacheKey = _generateCacheKey(mergedModules);
  
  // 检查缓存中是否已存在模块
  if (moduleCache[cacheKey]) {
    logger.info(`从缓存中获取模块: ${cacheKey}`);
    return moduleCache[cacheKey];
  }
  
  logger.info(`加载模块: ${Object.keys(mergedModules).join(', ')}`);
  
  // 预加载所有需要的模块（只加载一次）
  const moduleExports = await Promise.all(
    Object.entries(mergedModules).map(async ([name, importFuncOrCode]) => {
      try {
        let loadedModule;
        
        // 处理代码字符串
        if (typeof importFuncOrCode === 'string') {
          try {
            // 创建一个函数来执行代码字符串
            // 增加安全检查，防止执行危险代码
            if (importFuncOrCode.includes('import') || importFuncOrCode.includes('require') || importFuncOrCode.includes('eval')) {
              throw new Error('代码字符串包含不被允许的代码');
            }
            const moduleFunction = new Function(`return ${importFuncOrCode}`);
            loadedModule = moduleFunction();
            logger.info(`代码字符串模块加载成功: ${name}`);
          } catch (evalError: any) {
            logger.error(`代码字符串执行失败（${name}）:`, evalError);
            throw new Error(`代码字符串执行失败: ${evalError.message}`);
          }
        } 
        // 处理动态导入函数
        else {
          loadedModule = await importFuncOrCode();
          // 处理默认导出（如dayjs的默认导出是函数本身）
          loadedModule = loadedModule.default || loadedModule;
          logger.info(`动态导入模块加载成功: ${name}`);
        }
        
        return [name, loadedModule] as [string, any];
      } catch (error: any) {
        logger.error(`模块加载失败（${name}）:`, error);
        // 提供更详细的错误信息
        return [name, null] as [string, any]; // 返回null而不是错误函数
      }
    })
  );
  
  // 转为 { 模块名: 导出对象 } 格式
  const modules = Object.fromEntries(moduleExports);
  
  // 将模块存储到缓存中
  moduleCache[cacheKey] = modules;
  logger.info(`模块已缓存: ${cacheKey}`);
  
  return modules;
};

/**
 * 清除模块缓存
 * 
 * @example
 * // 清除所有模块缓存
 * clearModuleCache();
 */
const clearModuleCache = () => {
  const cacheKeys = Object.keys(moduleCache);
  logger.info(`清除模块缓存，共 ${cacheKeys.length} 个条目`);
  cacheKeys.forEach(key => {
    delete moduleCache[key];
  });
  logger.info('模块缓存已清除');
};

/**
 * 清除TypeScript类型注解
 * 
 * 该函数使用正则表达式移除代码字符串中的TypeScript类型注解，
 * 使代码可以被正确解析和执行。
 * 
 * @param {string} code - 带有类型注解的代码字符串
 * @returns {string} 清除类型注解后的代码字符串
 * 
 * @example
 * // 清除类型注解
 * const cleanedCode = _removeTypeAnnotations('(value: string): string => value');
 * // 返回: '(value) => value'
 */
const _removeTypeAnnotations = (code: string) => {
  // 使用正则表达式更高效地清除TypeScript类型注解
  return code
    // 移除参数类型注解 (param: Type) -> (param)
    .replace(/(\w+)\s*:\s*[^,)]+/g, '$1')
    // 移除返回类型注解 (param) => value: Type -> (param) => value
    .replace(/(\w+)\s*:\s*[^;}]+/g, '$1')
    // 标准化分号为逗号
    .replace(/;\s*/g, ', ')
    // 修复尾随逗号
    .replace(/,\s*}/g, ' }')
    .trim();
};

/**
 * 将函数字符串转换为可执行函数并注入模块
 * 
 * 该函数首先清除TypeScript类型注解，然后验证箭头函数格式，
 * 最后动态创建可执行函数并注入所需模块。
 * 
 * @param {string} funcStr - 函数字符串
 * @param {Object} modules - 已加载的模块对象
 * @returns {Function} 解析后的可执行函数
 * 
 * @example
 * // 转换函数字符串并注入模块
 * const modules = { dayjs: dayjsInstance };
 * const func = await stringToFunction('value => dayjs(value).format("YYYY-MM-DD")', modules);
 */
const stringToFunction = async (
  funcStr: string,
  modules: { [moduleName: string]: any }
) => {
  // 验证是否为箭头函数格式
  const arrowFuncRegex = /^\s*(?:\(([^)]*)\)|(\w+))\s*=>\s*([\s\S]*)$/;
  const match = funcStr.match(arrowFuncRegex);

  if (!match) {
    throw new Error(`格式错误，必须是箭头函数（如 "(a,b)=>..." 或 "a=>..."）`);
  }

  try {
    // 动态创建函数，注入所有模块 + 原始参数
    const moduleNames = Object.keys(modules);
    // 使用更安全的函数构造方式，避免直接执行用户代码
    const funcBody = `return function(${match[1] || match[2]}) { 
      // 执行原始render函数，确保模块和参数都能访问
      return (${funcStr})(...arguments); 
    }`;
    
    // 检查函数体是否包含潜在危险代码
    if (funcBody.includes('import') || funcBody.includes('require') || funcBody.includes('eval')) {
      throw new Error('函数体包含不被允许的代码');
    }
    
    const func = new Function(
      ...moduleNames, // 模块参数（如'dayjs', 'lodash'）
      funcBody
    )(...Object.values(modules)); // 传递模块实例

    return func;
  } catch (error: any) {
    logger.error('函数转换失败:', error);
    throw new Error(`函数转换失败: ${error.message}`);
  }
};

/**
 * 解析表格列配置，支持动态导入模块并注入到render函数
 * 
 * 该函数接收原始列配置和模块配置，加载所需模块，
 * 然后将字符串形式的render函数转换为可执行函数并注入模块。
 * 
 * @param {Array} rawColumns - 原始列配置（含字符串形式的render）
 * @param {ModuleConfig} moduleConfig - 模块配置（可选，覆盖默认配置）
 * @returns {Promise<Array>} 解析后的列配置（含可执行的render函数）
 * 
 * @example
 * // 使用默认模块配置
 * const columns = await parseColumns(rawColumns);
 * 
 * // 使用自定义模块配置
 * const customModuleConfig = {
 *   dayjs: () => import('dayjs'),
 *   lodash: () => import('lodash'),
 *   customModule: '({ customFunction: (x) => x * 2 })'
 * };
 * const columns = await parseColumns(rawColumns, customModuleConfig);
 * 
 * // 清除模块缓存
 * clearModuleCache();
 */
const parseColumns = async (
  rawColumns: any[],
  moduleConfig: ModuleConfig = {}
) => {
  // 合并默认配置和用户配置
  const mergedModules = { ...defaultModuleConfig, ...moduleConfig };

  // 处理列配置中的嵌入式模块定义
  rawColumns.forEach(column => {
    if (column.embeddedModules && typeof column.embeddedModules === 'object') {
      Object.entries<string>(column.embeddedModules).forEach(([moduleName, moduleCode]) => {
        // 检查模块是否已存在，避免覆盖
        if (!mergedModules[moduleName]) {
          mergedModules[moduleName] = moduleCode;
        } else {
          logger.warn(`嵌入式模块 ${moduleName} 已存在，跳过定义`);
        }
      });
    }
  });

  // 预加载所有需要的模块（只加载一次）
  const modules = await _loadModules(mergedModules);

  // 处理需要转换的函数字段
  const functionFields = ['render', 'expr'];

  // 解析render和expr函数并注入模块
  const parsedColumns = await Promise.all(
    rawColumns.map(async (column) => {
      const resultColumn = { ...column };

      for (const field of functionFields) {
        if (resultColumn[field] && typeof resultColumn[field] === 'string') {
          try {
            // 如果列配置中指定了所需的模块，则只注入这些模块
            let injectedModules = modules;
            if (resultColumn.requiredModules && Array.isArray(resultColumn.requiredModules)) {
              injectedModules = {};
              resultColumn.requiredModules.forEach((moduleName: string) => {
                if (modules[moduleName]) {
                  injectedModules[moduleName] = modules[moduleName];
                } else {
                  logger.warn(`模块 ${moduleName} 未在全局模块配置中找到`);
                }
              });
            }

            const func = await stringToFunction(resultColumn[field] as string, injectedModules);
            resultColumn[field] = func;
          } catch (error: any) {
            const fieldName = field === 'render' ? '渲染' : '表达式';
            logger.error(`解析${fieldName}失败（${resultColumn.key || resultColumn.dataIndex}）:`, error);
            logger.error(`原始代码: ${resultColumn[field]}`);
            resultColumn[field] = () => `[${fieldName}函数错误: ${error.message}]`;
          }
        }
      }
      return resultColumn;
    })
  );

  return parsedColumns;
};

export { clearModuleCache, parseColumns };

/**
 * 列配置类型定义
 * @typedef {Object} ColumnConfig
 * @property {string} title - 列标题
 * @property {string} key - 列的唯一标识
 * @property {string} dataIndex - 数据索引
 * @property {string} render - 渲染函数字符串
 * @property {RequiredModules} [requiredModules] - 该列渲染所需的模块列表
 * @property {EmbeddedModules} [embeddedModules] - 该列的嵌入式自定义模块定义
 * 
 * @example
 * // 列配置示例
 * const column = {
 *   title: '创建时间',
 *   dataIndex: 'createdAt',
 *   render: 'value => dayjs(value).format("YYYY-MM-DD HH:mm:ss")',
 *   requiredModules: ['dayjs']
 * };
 * 
 * @example
 * // 带嵌入式模块的列配置示例
 * const columnWithEmbeddedModule = {
 *   title: '计算值',
 *   dataIndex: 'value',
 *   render: 'value => customUtils.multiply(value, 2)',
 *   requiredModules: ['customUtils'],
 *   embeddedModules: {
 *     customUtils: '({ multiply: (a, b) => a * b })'
 *   }
 * };
 */
export type ColumnConfig = {
  title: string;
  key?: string;
  dataIndex?: string;
  render?: string | ((...args: any[]) => any);
  requiredModules?: RequiredModules;
  embeddedModules?: EmbeddedModules;
  [key: string]: any;
};